// Components==============
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMediaQ } from 'hooks/useMediaQ';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { useTranslate } from 'hooks/useTranslate';
import React, { useEffect, useRef, useState } from 'react';
import { TranslateType } from 'typings';
import './Dropdown.module.scss';
// =========================

type Props = {
  initialText: TranslateType;
  initialOptions: string[];
  translateOptions?: boolean;
  readOnly?: boolean;
  color?: 'grey' | 'white';
  disabled?: boolean;
  allowReset?: boolean;
  // notAllOptions?: boolean;
} & (
  | {
      selectedItems?: never;
      selectedItem: string;
      setSelectedItems?: never;
      setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
    }
  | {
      selectedItem?: never;
      selectedItems: string[];
      setSelectedItem?: never;
      setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
    }
);

export default function Dropdown({
  initialText,
  initialOptions,
  selectedItem,
  setSelectedItem,
  selectedItems,
  setSelectedItems,
  translateOptions,
  readOnly,
  color = 'white',
  disabled,
  allowReset,
}: // notAllOptions,
Props) {
  // dropdown logic
  const [isDropped, setIsDropped] = useState(false);
  const [options, setOptions] = useState(initialOptions);
  const [activeOption, setActiveOption] = useState(-1);
  const [inputValue, setInputValue] = useState('');

  const translate = useTranslate();

  useEffect(() => {
    if (selectedItems)
      setOptions((prev) =>
        prev.filter((option) => !selectedItems.includes(option))
      );
  }, [selectedItems?.length]);

  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions[0]]);

  useEffect(() => {
    setOptions(initialOptions);
  }, [JSON.stringify(initialOptions)]);

  function addOption(option: string) {
    if (!initialOptions.includes(option)) return;

    setActiveOption(-1);
    setInputValue('');
    setIsDropped(false);

    if (typeof selectedItem !== 'undefined' && setSelectedItem) {
      setSelectedItem(option);
      return;
    }

    if (setSelectedItems && selectedItems) {
      setSelectedItems((prev) => [...prev, option]);
      setOptions(initialOptions.filter((current) => current !== option));
    }
  }

  function removeOption(option: string) {
    setOptions((prev) => [...prev, option]);

    if (setSelectedItems)
      setSelectedItems((prev) => prev.filter((current) => current !== option));
  }

  function resetOptions(e: React.MouseEvent<SVGSVGElement>) {
    e.stopPropagation();
    setOptions(initialOptions);

    if (setSelectedItem) setSelectedItem('');
    if (setSelectedItems) setSelectedItems([]);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    setActiveOption(0);
    setIsDropped(true);

    if (typeof selectedItem !== 'undefined')
      setOptions(
        initialOptions
          .filter((option) => selectedItem !== option)
          .filter(
            (optionName) =>
              optionName
                .toLowerCase()
                .indexOf(e.currentTarget.value.toLowerCase()) > -1
          )
      );

    if (selectedItems)
      setOptions(
        initialOptions
          .filter((option) => !selectedItems.includes(option))
          .filter(
            (optionName) =>
              optionName
                .toLowerCase()
                .indexOf(e.currentTarget.value.toLowerCase()) > -1
          )
      );
  }

  const inputRef = useRef<HTMLInputElement>(null!);
  const inputAreaRef = useRef<HTMLDivElement>(null!);
  const ref = useRef<HTMLDivElement>(null!);

  useEffect(() => {
    if (!isDropped) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        e.preventDefault();
        addOption(options[activeOption]);
      } else if (e.key === 'ArrowUp') {
        if (activeOption === 0) {
          return;
        }
        ref.current.scrollTop += -35;
        setActiveOption((prev) => prev - 1);
      } else if (e.key === 'ArrowDown') {
        if (activeOption === options.length - 1) {
          return;
        }
        ref.current.scrollTop += +35;
        setActiveOption((prev) => prev + 1);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeOption, isDropped, inputValue]);

  const customOption = (option: string) =>
    translateOptions && option.includes('terms.') ? translate(option) : option;

  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside({
    refs: [dropdownRef],
    handler: () => setIsDropped(false),
    condition: isDropped,
  });

  const query = useMediaQ('min', 1024);

  return (
    <div
      styleName={`wrapper ${color}`}
      style={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'all',
      }}
      ref={dropdownRef}
    >
      <div
        styleName="input-area"
        ref={inputAreaRef}
        style={{
          gridTemplateColumns:
            selectedItems?.length || (allowReset && selectedItem?.length)
              ? '1fr auto auto auto'
              : '1fr auto auto',
        }}
        onClick={() => {
          inputRef.current.focus();
          setIsDropped((prev) => !prev);
        }}
      >
        <input
          // @ts-ignore
          placeholder={
            selectedItem?.length
              ? customOption(selectedItem)
              : selectedItems?.length === 1
              ? customOption(selectedItems[0])
              : selectedItems && selectedItems?.length > 1
              ? `${selectedItems?.length} items`
              : initialText
          }
          styleName={
            selectedItem?.length || selectedItems?.length ? 'single-item' : ''
          }
          ref={inputRef}
          type="text"
          onChange={handleInputChange}
          value={inputValue}
          readOnly={readOnly || !query}
          style={{ cursor: readOnly || !query ? 'pointer' : '' }}
        />
        {selectedItems?.length || (allowReset && selectedItem?.length) ? (
          <FontAwesomeIcon
            icon={faTimes}
            onClick={resetOptions}
            styleName="reset"
          />
        ) : null}
        <div styleName="bar" />
        <FontAwesomeIcon icon={faAngleDown} />
      </div>
      {isDropped && (
        <div
          styleName="dropdown-area"
          ref={ref}
          style={{ width: inputAreaRef.current.clientWidth }}
        >
          {selectedItems &&
            selectedItems?.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => removeOption(option)}
                styleName="drop-option selected"
              >
                {customOption(option)}
                <FontAwesomeIcon icon={faTimes} />
              </button>
            ))}
          {options.map((option, index) => (
            <button
              type="button"
              onClick={() => addOption(option)}
              styleName={`drop-option ${
                index === activeOption ? 'active' : ''
              } ${selectedItem === option ? 'selected' : ''}`}
              key={option}
              onMouseEnter={() => setActiveOption(index)}
              onMouseLeave={() => setActiveOption(-1)}
            >
              {customOption(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
